import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useLanguages } from '../../hooks/useLanguages';
import { BookmarksService } from '../../services/BookmarksService';

// Bookmarks page — mirrors mobile's BookmarksScreen (lib/screens/bookmarks/bookmarks_screen.dart).
// Lists everything saved via BookmarksService for the current language pair,
// with a speak + remove action per row.

const TYPE_ICONS = {
  vocab: '🔤',
  story: '📖',
  conversation: '🗣️',
};

const SpeakerIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.816L4.766 14H3a1 1 0 01-1-1V7a1 1 0 011-1h1.766l3.617-2.816a1 1 0 011.017-.108zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.415z" clipRule="evenodd" />
  </svg>
);

const RemoveIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
  </svg>
);

export default function BookmarksPage() {
  const { t } = useTranslation();
  const { languagePrefs } = useAuth();
  const { languages } = useLanguages();
  const langMap = useMemo(() => Object.fromEntries(languages.map((l) => [l.code, l])), [languages]);

  const nativeCode = languagePrefs?.nativeLanguage || '';
  const learnCode = languagePrefs?.targetLanguage || '';
  const nativeLang = langMap[nativeCode] || { code: nativeCode, label: nativeCode, dir: 'ltr' };
  const targetLang = langMap[learnCode] || { code: learnCode, label: learnCode, dir: 'ltr' };
  const langPair = `${nativeCode}_${learnCode}`;

  const [items, setItems] = useState(null);

  function load() {
    if (!langPair) return;
    setItems(BookmarksService.all(langPair));
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [langPair]);

  function speak(text, ttsCode) {
    if (!text || typeof window === 'undefined' || !window.speechSynthesis) return;
    try {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = ttsCode || '';
      utterance.rate = 0.95;
      utterance.onerror = () => {};
      window.speechSynthesis.speak(utterance);
    } catch {}
  }

  function handleRemove(item) {
    BookmarksService.remove(item);
    load();
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6">
        <div className="mb-5 flex items-center justify-between">
          <h1 className="text-2xl font-black text-slate-900">{t('bookmarks.title', 'Bookmarks')}</h1>
          <span className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-500 shadow-sm">
            {nativeLang.label} → {targetLang.label}
          </span>
        </div>

        {items === null ? (
          <div className="space-y-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-16 animate-pulse rounded-2xl bg-slate-100" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
            <span className="text-5xl">🔖</span>
            <p className="mt-3 text-sm text-slate-500">
              {t('bookmarks.empty', 'Save words, stories, and conversation lines to find them here.')}
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-2.5">
            {items.map((item) => (
              <div
                key={item.id}
                className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-3.5 py-3 shadow-sm"
              >
                <span className="text-xl shrink-0" aria-hidden="true">
                  {item.emoji || TYPE_ICONS[item.type] || '🔖'}
                </span>
                <div className="min-w-0 flex-1">
                  <p dir={nativeLang.dir || 'ltr'} className="truncate text-xs text-slate-400">
                    {item.nativeText}
                  </p>
                  <p dir={targetLang.dir || 'ltr'} className="truncate text-base font-bold text-slate-800">
                    {item.targetText}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => speak(item.targetText, targetLang.tts)}
                  aria-label={t('bookmarks.speak', 'Speak')}
                  className="shrink-0 rounded-full bg-indigo-50 p-2 text-indigo-500 transition-colors hover:bg-indigo-100"
                >
                  <SpeakerIcon />
                </button>
                <button
                  type="button"
                  onClick={() => handleRemove(item)}
                  aria-label={t('bookmarks.remove', 'Remove bookmark')}
                  className="shrink-0 rounded-full p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
                >
                  <RemoveIcon />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
